import { useEffect, useState } from "react";
import { Save, Loader2, Calculator, FileText, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const RATE_FIELDS = [
  { key: "rate_wholesale", label: "Wholesale rate", hint: "e.g. 0.001 = 1 mill" },
  { key: "rate_retail", label: "Retail rate", hint: "e.g. 0.0015 = 1.5 mills" },
  { key: "rate_biz", label: "Business privilege rate", hint: "e.g. 0.004 = 4 mills" },
];
const THRESHOLD_FIELDS = [
  { key: "threshold_wholesale", label: "Wholesale minimum tax", hint: "Below this, line rounds to $0" },
  { key: "threshold_retail", label: "Retail minimum tax", hint: "Below this, line rounds to $0" },
  { key: "threshold_biz", label: "Business privilege minimum tax", hint: "Below this, line rounds to $0" },
];
const FEE_FIELDS = [
  { key: "license_fee", label: "License fee (per category)", hint: "Flat fee charged per selected category" },
  { key: "penalty_rate", label: "Penalty rate", hint: "e.g. 0.10 = 10%" },
  { key: "interest_rate", label: "Interest rate (per month)", hint: "e.g. 0.0125 = 1.25%/month" },
];

const Section = ({ title, children }) => (
  <div className="border border-slate-200 rounded-md p-5 space-y-4">
    <p className="text-xs uppercase tracking-wide text-slate-400">{title}</p>
    <div className="grid sm:grid-cols-3 gap-4">{children}</div>
  </div>
);

const NumField = ({ f, cfg, setCfg }) => (
  <div className="space-y-1.5">
    <Label className="text-sm">{f.label}</Label>
    <Input
      type="number"
      step="any"
      value={cfg[f.key] ?? ""}
      onChange={(e) => setCfg((c) => ({ ...c, [f.key]: e.target.value }))}
      data-testid={`taxcfg-${f.key}`}
    />
    <p className="text-xs text-slate-400">{f.hint}</p>
  </div>
);

const FORM_FIELDS = [
  { key: "office_name", label: "Payable to (office name)", hint: "e.g. Cheltenham Township Finance Officer" },
  { key: "treasurer", label: "Office / treasurer line", hint: "Shown under the township name" },
  { key: "addr1", label: "Address line 1", hint: "Street address" },
  { key: "addr2", label: "Address line 2", hint: "City, State ZIP" },
  { key: "phone", label: "Phone", hint: "Contact number on the form" },
  { key: "final_year", label: "Final return year", hint: "e.g. 2025" },
  { key: "est_year", label: "Estimated return year", hint: "e.g. 2026" },
  { key: "due", label: "Due date text", hint: "e.g. April 18, 2026" },
  { key: "tax_collector", label: "Tax Collector name", hint: "Printed on the form (leave blank for a write-in line)" },
];

export default function AdminTaxConfig() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [savingForm, setSavingForm] = useState(false);

  useEffect(() => {
    api
      .get("/settings/taxconfig")
      .then((res) => setCfg(res.data))
      .catch(() => toast.error("Failed to load tax configuration"))
      .finally(() => setLoading(false));
    api
      .get("/settings/formconfig")
      .then((res) => setForm(res.data))
      .catch(() => {});
  }, []);

  const saveForm = async () => {
    setSavingForm(true);
    try {
      await api.put("/admin/settings/formconfig", form);
      toast.success("Form header saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingForm(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        rate_wholesale: parseFloat(cfg.rate_wholesale) || 0,
        rate_retail: parseFloat(cfg.rate_retail) || 0,
        rate_biz: parseFloat(cfg.rate_biz) || 0,
        threshold_wholesale: parseFloat(cfg.threshold_wholesale) || 0,
        threshold_retail: parseFloat(cfg.threshold_retail) || 0,
        threshold_biz: parseFloat(cfg.threshold_biz) || 0,
        license_fee: parseFloat(cfg.license_fee) || 0,
        penalty_rate: parseFloat(cfg.penalty_rate) || 0,
        interest_rate: parseFloat(cfg.interest_rate) || 0,
        confirmation_prefix: (cfg.confirmation_prefix || "TAX").trim(),
      };
      await api.put("/admin/settings/taxconfig", payload);
      toast.success("Tax configuration saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg)
    return (
      <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900 flex items-center gap-2">
            <Calculator className="w-6 h-6 text-[#1E3A8A]" /> Tax Configuration
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Set your township's rates and fees. These drive every tax calculation and the generated
            PDF. Existing filings keep the amounts they were computed with.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="save-taxcfg-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save configuration
        </Button>
      </div>

      <div className="grid gap-6 max-w-3xl">
        <Section title="Tax rates">
          {RATE_FIELDS.map((f) => (
            <NumField key={f.key} f={f} cfg={cfg} setCfg={setCfg} />
          ))}
        </Section>
        <Section title="Minimum tax thresholds">
          {THRESHOLD_FIELDS.map((f) => (
            <NumField key={f.key} f={f} cfg={cfg} setCfg={setCfg} />
          ))}
        </Section>
        <Section title="Fees, penalty & interest">
          {FEE_FIELDS.map((f) => (
            <NumField key={f.key} f={f} cfg={cfg} setCfg={setCfg} />
          ))}
        </Section>
        <div className="border border-slate-200 rounded-md p-5 space-y-4">
          <p className="text-xs uppercase tracking-wide text-slate-400">Confirmation numbers</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Confirmation prefix</Label>
              <Input
                value={cfg.confirmation_prefix ?? ""}
                onChange={(e) => setCfg((c) => ({ ...c, confirmation_prefix: e.target.value }))}
                data-testid="taxcfg-confirmation_prefix"
                placeholder="CHT"
              />
              <p className="text-xs text-slate-400">Shown as PREFIX-YYYYMMDD-XXXXXX on each filing.</p>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-md p-5 space-y-4" data-testid="formheader-block">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm font-medium text-slate-800 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#1E3A8A]" /> Printable Form Header
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                Controls the mailer heading, payable-to block, and dates on the downloadable paper form.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="border-slate-300"
                onClick={() => window.open(`${API}/forms/blank-pdf`, "_blank", "noopener")}
                data-testid="preview-blank-form-btn"
              >
                <Printer className="w-4 h-4 mr-2" /> Preview PDF
              </Button>
              <Button
                variant="outline"
                className="border-slate-300"
                onClick={() => window.open(`${API}/forms/blank-docx`, "_blank", "noopener")}
                data-testid="download-blank-docx-btn"
              >
                <FileText className="w-4 h-4 mr-2" /> Word
              </Button>
              <Button
                onClick={saveForm}
                disabled={savingForm || !form}
                className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                data-testid="save-formheader-btn"
              >
                {savingForm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save header
              </Button>
            </div>
          </div>
          {form && (
            <div className="grid sm:grid-cols-2 gap-4">
              {FORM_FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label className="text-sm">{f.label}</Label>
                  <Input
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                    data-testid={`formcfg-${f.key}`}
                  />
                  <p className="text-xs text-slate-400">{f.hint}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
